<template>
  <div ref="view" style="height: 100%">
    <slot></slot>
  </div>
</template>

<script>
import esriComponent from '../../mixins/esri-component';

import _isEqual from 'lodash/isEqual';

import {loadModules} from 'esri-loader';

export default {
  inject: ['getMap'],
  provide () {
    return {
      getView: this.getView,
      getGraphics: this.getGraphics
    };
  },
  mixins: [esriComponent],
  props: {
    constraints: {
      type: Object,
      required: false,
      default: undefined
    },
    extent: {
      type: Object,
      required: false,
      default: undefined
    },
    scale: {
      type: Number,
      required: false,
      default: undefined
    },
    ready: {
      type: Boolean,
      required: false,
      default: false
    },
    updating: {
      type: Boolean,
      required: false,
      default: false
    },
    ui: {
      type: Object,
      required: false,
      default: undefined
    },
  },
  watch: {
    extent: {
      handler (extent) {
        this.setMapViewExtent(extent);
      },
      deep: true
    },
    scale (scale) {
      this.setMapViewScale(scale);
    }
  },
  methods: {
    /**
     * @Override
     */
    beforeInit () {
      return this.wait(this.getMap);
    },
    /**
     * @Override
     */
    async loadEsriModules () {
      return await loadModules(['esri/views/MapView', 'esri/geometry/Extent']);
    },
    /**
     * @returns {Promise<MapView>}
     * @Override
     */
    async createEsriObject () {
      const [MapView] = this.$esriModules;
      const view = new MapView({
        container: this.$refs.view,
        extent: this.extent
      });
      if (this.constraints) {
        view.constraints = this.constraints;
      }
      if (this.ui) {
        view.ui = this.ui;
      }
      return view;
    },
    /**
     * @Override
     */
    async mount () {
      this.$esriWatcher = [
        this.$esriObject.watch('extent', value => {
          this.updateValue('extent', value.toJSON());
        }),
        this.$esriObject.watch('scale', value => {
          this.updateValue('scale', value);
        }),
        this.$esriObject.watch('ready', value => {
          this.updateValue('ready', value);
        }),
        this.$esriObject.watch('updating', value => {
          this.updateValue('updating', value);
        }),
        this.$esriObject.on('pointer-move', event => {
          this.$emit('pointer-move', this.$esriObject.toMap({
            x: event.x,
            y: event.y
          }).toJSON());
        })
      ];

      const map = this.getMap();
      if (map) {
        this.$esriObject.map = map;
      }
    },
    /**
     * @Override
     */
    async unmount () {
      this.$esriWatcher.forEach(element => {
        element.remove();
      });
    },
    getView () {
      return this.$esriObject;
    },
    getGraphics () {
      return this.$esriObject ? this.$esriObject.graphics : null;
    },
    async setMapViewExtent (extent) {
      await this.waitForEsriObject();
      if (!_isEqual(this.$esriObject.extent.toJSON(), extent)) {
        const [MapView, Extent] = this.$esriModules;
        this.$esriObject.goTo(new Extent(extent));
      }
    },
    async setMapViewScale (scale) {
      await this.waitForEsriObject();
      if (this.$esriObject.scale !== scale) {
        this.$esriObject.goTo({
          scale: scale
        });
      }
    }
  }
};
</script>
